<app-header></app-header>
<main class="container mx-auto px-4 pb-6 lg:px-6 lg:py-8">
        <!-- Hero Section -->
        <section class="mt-4 mb-8 lg:mb-12">
            <div class="bg-gradient-to-r from-wattpad-primary to-wattpad-secondary rounded-xl p-6 lg:p-8 text-white">
                <div class="flex flex-col lg:flex-row gap-6 items-center">
                    <div class="lg:w-1/3 flex justify-center">
                        <div class="bg-white/20 p-6 rounded-full w-48 h-48 flex items-center justify-center">
                            <i class="fas fa-shield-alt text-6xl"></i>
                        </div>
                    </div>
                    <div class="lg:w-2/3 text-center lg:text-left">
                        <h1 class="text-2xl lg:text-4xl font-bold mb-2">🔒 Privacy Policy – Funtel Stories</h1>
                        <p class="text-lg lg:text-2xl mb-4">Last Updated: 28th March, 2025</p>
                        
                        <p class="text-sm lg:text-base mb-6">
                            We welcome you to Funtel Stories and thank you for being part of our storytelling community. 
                            We encourage you to carefully read this Privacy Policy to understand how we collect, use, store, and protect your personal data. 
                            Transparency and respect for your privacy are central to how we operate.
                        </p>
                        
                       
                    </div>
                </div>
            </div>
        </section>

        <!-- Important Notice -->
        <section class="mb-8 lg:mb-12">
            <div class="bg-wattpad-accent/10 border border-wattpad-accent/20 rounded-xl p-6">
                <div class="flex items-start">
                    <i class="fas fa-exclamation-circle text-wattpad-accent text-xl mt-1 mr-4"></i>
                    <div>
                        <h3 class="font-bold text-lg mb-2">Important Notice</h3>
                        <p class="text-wattpad-dark dark:text-gray-300">
                            If you do not agree with the practices described below, you are advised not to use the Funtel Stories website and/or application.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Privacy Policy Content -->
        <section class="mb-8 lg:mb-12">
            <div class="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                <!-- Scope -->
                <div class="policy-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">1. Scope</h2>
                    <p class="mb-4">
                        This Privacy Policy applies to the use of the Funtel Stories website (stories.funtelstudio.com) ("Website") and the official Funtel Stories mobile application on Android and iOS ("Application") operated by Funtel Stories, a registered partnership firm ("Firm", "We", "Our", or "Us").
                    </p>
                    <p class="mb-4">
                        Through the Website/Application, Users can:
                    </p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Publish and read literary works (stories, poems, articles, etc.),</li>
                        <li>View Firm Content published by Funtel Stories,</li>
                        <li>Post reviews, comments, and messages ("Inputs"), and</li>
                        <li>Use various features offered by the Firm ("Services").</li>
                    </ul>
                    <p>
                        Published Works and Firm Content together are referred to as Content.
                    </p>
                    <p class="mt-4">
                        This Privacy Policy is a part of and should be read with the Terms of Service. By using our Services, you agree to this Policy.
                    </p>
                </div>

                <!-- Information We Collect -->
                <div class="policy-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">2. Information We Collect</h2>
                    <p class="mb-4">
                        We collect both personally identifiable information ("Personal Information") and non-identifiable information ("Usage Data") to improve the Services.
                    </p>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">2.1 Registration / Login Data</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Name, email address, mobile number.</li>
                        <li>Social login details (Google/Facebook/Apple) where applicable.</li>
                        <li>Optional profile details (gender, city, bio, display photo).</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">2.2 Usage Data</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Stories published, reviews/comments posted.</li>
                        <li>Pages visited, reading time, interactions, and search preferences.</li>
                        <li>Participation in contests and monetization programs.</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">2.3 Device Data</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Device type, operating system, browser type/version.</li>
                        <li>Unique device identifier, IP address.</li>
                        <li>Location and language preference (if allowed by device settings).</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">2.4 Payment Data</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Billing details, bank account details (for Author payouts).</li>
                        <li>Payment gateway details (when purchasing coins/credits or subscriptions).</li>
                    </ul>
                    
                    <h3 class="text-lg font-bold mb-3 mt-6">2.5 Customer Support</h3>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Information provided during support requests or queries.</li>
                    </ul>
                </div>

                <!-- How We Use Your Information -->
                <div class="policy-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">3. How We Use Your Information</h2>
                    <p class="mb-4">
                        We use collected information to:
                    </p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>Enable you to publish, read, and interact with Content.</li>
                        <li>Process payments, subscriptions, and Author payouts.</li>
                        <li>Enforce our Terms of Service and protect against fraud.</li>
                        <li>Send important notifications (account, security, updates).</li>
                        <li>Personalize recommendations and improve the User experience.</li>
                        <li>Improve Services through research, analysis, and surveys.</li>
                        <li>Comply with applicable legal obligations.</li>
                    </ul>
                </div>

                <!-- Sharing of Information -->
                <div class="policy-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">4. Sharing of Information</h2>
                    <p class="mb-4">
                        We never sell or rent User data. Information may be shared with:
                    </p>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li><strong>Service Providers</strong> – Payment gateways (e.g., Razorpay, Google Play, Apple), hosting (AWS/Firebase), analytics providers (Google Analytics, Amplitude), and notification services.</li>
                        <li><strong>Legal Authorities</strong> – When required by law, government agencies, or court orders.</li>
                        <li><strong>Corporate Restructuring</strong> – In case of merger, acquisition, or transfer of assets.</li>
                        <li><strong>Community Features</strong> – Inputs (like reviews, comments, likes) are public and can be viewed by other Users.</li>
                    </ul>
                </div>

                <!-- Storage & Security -->
                <div class="policy-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">5. Storage & Security</h2>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li>User data is stored on secure servers (e.g., AWS India, Google Firebase).</li>
                        <li>Passwords are encrypted and stored securely.</li>
                        <li>Internal staff access is on a strict need-to-know basis.</li>
                        <li>Despite strong safeguards, no system is 100% secure. Users are advised to protect their account credentials.</li>
                    </ul>
                </div>

                <!-- Cookies & Tracking -->
                <div class="policy-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">6. Cookies & Tracking</h2>
                    <p class="mb-4">
                        Cookies and similar technologies may be used to improve functionality, track usage, and personalize experiences.
                    </p>
                    <p>
                        Users can opt out of cookies through their browser, but this may limit some features.
                    </p>
                </div>

                <!-- User Rights -->
                <div class="policy-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">7. User Rights</h2>
                    <ul class="list-disc pl-6 mb-4 space-y-2">
                        <li><strong>Registration Choice</strong>: You may choose not to register, but certain Services may be unavailable.</li>
                        <li><strong>Modify/Delete</strong>: You can edit or delete profile details via account settings.</li>
                        <li><strong>Deletion</strong>: You can request deletion of your account and personal data by emailing us at support&#64;funtelstories.com. Some data may be retained as required by law.</li>
                        <li><strong>Notifications</strong>: You may opt out of promotional notifications via settings, though critical account messages will still be sent.</li>
                    </ul>
                </div>

                <!-- Changes to Privacy Policy -->
                <div class="policy-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">8. Changes to Privacy Policy</h2>
                    <p class="mb-4">
                        We may update this Privacy Policy from time to time. The revised version will be posted on the Website/Application with an updated date. Continued use after changes implies acceptance of the updated Policy.
                    </p>
                </div>

                <!-- Grievance Redressal -->
                <div class="policy-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50" id="contact">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">9. Grievance Redressal</h2>
                    <p class="mb-4">
                        For any concerns or grievances relating to privacy or misuse of your data, you may contact:
                    </p>
                    <div class="bg-wattpad-primary/10 p-4 rounded-lg">
                        <h3 class="font-bold mb-2">Grievance Officer</h3>
                        <p class="mb-1">📧 Email: support&#64;funtelstories.com</p>
                        <p>📍 Address: 8th Floor, Balaji Infotech Park, Plot No. A-278, Wagle Industrial Estate, Road No. 16 A, Lane Next to Wagale Police Station, Thane (W) - 400 604</p>
                    </div>
                    <p class="mt-4">
                        We will acknowledge complaints within 48 hours and resolve them within 15 days.
                    </p>
                </div>

                <!-- Conflict -->
                <div class="policy-section mb-8 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50" id="hindi-version">
                    <h2 class="text-xl lg:text-2xl font-bold mb-4 text-wattpad-primary">10. Conflict</h2>
                    <p class="mb-4">
                        In case of any conflict between the English version of this Privacy Policy and a translated version (including Hindi), the English version shall prevail.
                    </p>
                    
                </div>
            </div>
        </section>

       
    </main>
